/**
 * A box's diagnosis in the library Drawer (admin-design-r1 question 5): `?open=box:<founder slug>` on any admin page
 * opens it, so a push, the Fleet table, Today and the founder page all link to the same address. Mounted once by the
 * admin shell. Inside: what's wrong with its steps, every signal, the actions (each asks first and runs once, audited
 * as you), and what was done lately. Refreshes every 15 s while open; "Check again" asks the vendors afresh.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { auditWords, confirmWords, problemsFirst, STATE_WORDS } from '../../domain/diagnosis'
import { ago, when } from '../../domain/format'
import type { ActionAudit, ActionKey, ActionView, Diagnosis as DiagnosisData } from '../../domain/schemas'
import { lightLook, severityLook } from '../../domain/tones'
import { visibleInterval } from '../../infra/visibleInterval'
import { Button, Card, ConfirmAction, DataTable, Drawer, EmptyState, ErrorNote, Heading, Inline, Light, Skeleton, Stack, Text, useNav, useParam } from '../../ui'
import { useApi } from '../api-context'
import { useLoad } from '../useLoad'
import { useNow } from '../useNow'

/** The panel refreshes this often while open (the API caches vendor answers for 30 s). */
const DIAGNOSIS_POLL_MS = 15_000

/** `box:fieldday` → `fieldday`; anything else → null. */
export const openBoxSlug = (open: string | null) => (open?.startsWith('box:') ? open.slice(4) || null : null)

interface ActionRun {
  phase: 'running' | 'ok' | 'failed'
  message: string
  audit: ActionAudit | null
}

/** The drawer itself: reads the address, finds the box by its founder's slug, and draws the diagnosis. */
export function BoxDrawer() {
  const { api } = useApi()
  const open = useParam('open')
  const slug = openBoxSlug(open.value)
  // One fleet read on open, to turn the slug into the live box.
  const fleet = useLoad(() => (slug ? api.fleet() : Promise.resolve(null)), [api, slug])
  const row = slug ? fleet.data?.boxes.find((r) => r.founder.slug === slug) : undefined
  return (
    <Drawer id={slug} onClose={open.clear} title={() => (row ? `${row.founder.name}'s box` : 'Box')}>
      {(s) =>
        row ? (
          <DiagnosisBody boxId={row.boxId} founder={row.founder} />
        ) : fleet.error ? (
          <ErrorNote reason={fleet.error.message} onRetry={fleet.reload} />
        ) : fleet.data ? (
          <EmptyState title="No live box.">Nothing in the fleet belongs to {s}. A box shows up once a run creates it.</EmptyState>
        ) : (
          <Stack gap="sm">
            <Skeleton width="60%" />
            <Skeleton width="85%" />
          </Stack>
        )
      }
    </Drawer>
  )
}

/** The diagnosis while open: loads at once, every 15 s while visible, and fresh (vendors asked again) on demand. */
function useDiagnosis(boxId: string) {
  const { api } = useApi()
  const [data, setData] = useState<DiagnosisData>()
  const [error, setError] = useState<Error>()
  const [loading, setLoading] = useState(false)
  const seq = useRef(0)
  const load = useCallback(
    (fresh = false) => {
      const n = ++seq.current
      setLoading(true)
      api.diagnosis(boxId, fresh).then(
        (d) => n === seq.current && (setData(d), setError(undefined), setLoading(false)),
        (e: unknown) => n === seq.current && (setError(e instanceof Error ? e : new Error(String(e))), setLoading(false)),
      )
    },
    [api, boxId],
  )
  useEffect(() => {
    load()
    return visibleInterval(() => load(), DIAGNOSIS_POLL_MS)
  }, [load])
  return { data, error, loading, load }
}

function DiagnosisBody({ boxId, founder }: { boxId: string; founder: { id: string; name: string } }) {
  const { api } = useApi()
  const nav = useNav()
  const d = useDiagnosis(boxId)
  const now = useNow(1000)
  const [runs, setRuns] = useState<Partial<Record<ActionKey, ActionRun>>>({})

  async function run(key: ActionKey) {
    setRuns((r) => ({ ...r, [key]: { phase: 'running', message: '', audit: null } }))
    try {
      const res = await api.boxAction(boxId, key)
      setRuns((r) => ({ ...r, [key]: { phase: 'ok', message: res.message, audit: res.audit } }))
    } catch (e) {
      setRuns((r) => ({ ...r, [key]: { phase: 'failed', message: e instanceof Error ? e.message : String(e), audit: null } }))
    }
    // What the action changed, and its line in the audit log.
    d.load(true)
  }

  const data = d.data
  const busy = Object.values(runs).some((r) => r?.phase === 'running')
  const action = (view: ActionView, label?: string) => <DiagnosisAction key={`${view.key}-${label ?? ''}`} view={view} label={label} founder={founder.name} run={runs[view.key]} busy={busy} now={now} onRun={run} />

  return (
    <Stack gap="lg">
      <Inline justify="between">
        <Text mono size="xs" tone="muted">
          {data ? `Checked ${ago(data.at, now)}` : d.loading ? 'Checking…' : ''}
        </Text>
        <Inline gap="xs">
          <Button variant="ghost" size="xs" onClick={() => nav.navigate(`/founders/${founder.id}`)}>
            {founder.name}'s page
          </Button>
          <Button variant="line" size="xs" onClick={() => d.load(true)} disabled={d.loading}>
            {d.loading && data ? 'Checking…' : 'Check again'}
          </Button>
        </Inline>
      </Inline>

      {d.error && <ErrorNote title={data ? "Couldn't refresh." : "This didn't load."} reason={d.error.message} stale={data ? 'Showing the last answer.' : undefined} onRetry={() => d.load(true)} />}
      {!data && !d.error && (
        <Stack gap="sm">
          <Text tone="muted">Asking the box, DigitalOcean, Cloudflare and Railway…</Text>
          <Skeleton width="70%" />
          <Skeleton width="90%" />
        </Stack>
      )}

      {data && (
        <>
          <Stack gap="sm" as="section" label="What's wrong">
            <Heading level={3}>What's wrong</Heading>
            {data.findings.length === 0 ? (
              <EmptyState kind="all-clear" title="Nothing wrong found.">Every signal that could be checked is fine.</EmptyState>
            ) : (
              data.findings.map((f) => (
                <Card key={f.id}>
                  <Stack gap="sm">
                    <Inline gap="sm" wrap={false} align="start">
                      <Light state={severityLook(f.severity)} word={f.severity === 'down' ? 'problem' : f.severity === 'warn' ? 'warning' : 'note'} />
                      <Text weight="head">{f.title}</Text>
                    </Inline>
                    <Text size="base">{f.explanation}</Text>
                    <Stack as="ol" gap="sm" label="Try this">
                      {f.steps.map((s, i) => {
                        const view = s.action ? data.actions.find((a) => a.key === s.action) : undefined
                        return (
                          <li key={i}>
                            <Stack gap="xs">
                              {view ? action(view, s.label) : <Text size="base">{`${i + 1}. ${s.label}`}</Text>}
                              {s.command && (
                                <Text mono size="sm" tone="muted">
                                  {s.command}
                                </Text>
                              )}
                            </Stack>
                          </li>
                        )
                      })}
                    </Stack>
                  </Stack>
                </Card>
              ))
            )}
          </Stack>

          <Stack gap="sm" as="section" label="Signals">
            <Heading level={3}>Signals</Heading>
            <DataTable
              label="Signals"
              framed={false}
              rows={problemsFirst(data.lights)}
              rowKey={(l) => l.key}
              empty={<EmptyState title="No signals yet.">The box reports them once its agent connects.</EmptyState>}
              columns={[
                { key: 'name', label: 'Signal', narrow: 'main', render: (l) => <Light state={lightLook(l.state)} label={l.label} word={STATE_WORDS[l.state]} /> },
                { key: 'state', label: 'State', narrow: 'end', render: (l) => <Text mono size="xs" tone={l.state === 'down' ? 'down' : 'muted'}>{STATE_WORDS[l.state]}</Text> },
                { key: 'detail', label: 'What we saw', narrow: 'below', narrowLabel: false, render: (l) => <Text size="sm" tone="muted">{l.detail}</Text> },
                { key: 'at', label: 'When', narrow: 'hide', align: 'end', render: (l) => <Text mono size="xs" tone="muted">{l.at ? ago(l.at, now) : ''}</Text> },
              ]}
            />
          </Stack>

          <Stack gap="sm" as="section" label="Actions">
            <Heading level={3}>Actions</Heading>
            <Text size="sm" tone="muted">
              One per box every {data.actionWindowSec} seconds. Each is recorded with your email.
            </Text>
            {data.actions.map((a) => action(a))}
          </Stack>

          {data.recent.length > 0 && (
            <Stack gap="sm" as="section" label="Recent actions">
              <Heading level={3}>Recent actions</Heading>
              {data.recent.map((a) => (
                <Inline key={`${a.at}-${a.action}`} gap="sm" align="baseline">
                  <Text mono size="xs" tone="muted">
                    {when(a.at, now)}
                  </Text>
                  <Text size="sm" tone={a.outcome === 'ok' ? 'default' : 'down'}>
                    {auditWords(a)}
                  </Text>
                  {a.message && (
                    <Text size="xs" tone="muted">
                      {a.message}
                    </Text>
                  )}
                </Inline>
              ))}
            </Stack>
          )}
        </>
      )}
    </Stack>
  )
}

/** One action: the library ConfirmAction with the action's own question, then what happened and its audit line. */
function DiagnosisAction({ view, label, founder, run, busy, now, onRun }: { view: ActionView; label?: string; founder: string; run: ActionRun | undefined; busy: boolean; now: number; onRun: (k: ActionKey) => void }) {
  const words = confirmWords(view, founder)
  return (
    <ConfirmAction
      label={label ?? view.label}
      strength="confirm"
      question={words.question}
      yes={words.yes}
      onConfirm={() => onRun(view.key)}
      disabled={!view.enabled || busy}
      reason={view.enabled ? null : view.reason}
      working={run?.phase === 'running'}
    >
      {run?.phase === 'ok' && (
        <span role="status">
          <Text size="sm" tone="accent">
            {run.message}
            {run.audit ? ` · ${auditWords(run.audit)} · ${ago(run.audit.at, now)}` : ''}
          </Text>
        </span>
      )}
      {run?.phase === 'failed' && (
        <span role="alert">
          <Text size="sm" tone="down">
            {run.message}
          </Text>
        </span>
      )}
    </ConfirmAction>
  )
}
